import * as angular from 'angular';
import { ORIENTATION } from '../bg-splitter.controller';
import { EventService } from '@go-modules/services/event/event.service';
import { Subject, takeUntil } from 'rxjs';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { FeedbackSessionService } from '@go-modules/services/feedback-session/feedback-session.service';

/* @ngInject */
export const BgSplitterHandleController = function (
	$element: ng.IRootElementService,
	WebStorage,
	$window: ng.IWindowService,
	$scope: ng.IScope,
	$translate: ng.translate.ITranslateService,
	eventService: EventService,
	ngxFeedbackSessionService: FeedbackSessionService
) {
	const vm = this;
	const storage = new WebStorage('splitter');
	let destroy$: Subject<void>;
	let handleId,
		handleWidth,
		handleHeight,
		prev,
		next,
		accessibilityElem: HTMLSpanElement,
		dragging: {
			offsetX: number;
			offsetY: number;
		} | null = null;

	vm.grabbed = false;
	vm.isDragging = false;
	vm.ariaValueNow = 0;
	vm.descriptionId = `bg-splitter-description-${$scope.$id}`;

	const clearStyles = {
		top: '',
		bottom: '',
		right: '',
		left: '',
		height: '',
		width: ''
	};

	// Collapsed panel spacer height on mobile (compact mode, no tabs)
	const PANEL_SPACER_HEIGHT = 18;
	const DEFAULT_HANDLE_HEIGHT = 5;
	const HEIGHT_REFLOW_BREAKPOINT = 256;

	// Track previous height-reflow state to detect transitions
	let wasInHeightReflow = window.innerHeight <= HEIGHT_REFLOW_BREAKPOINT;

	// At height-reflow, CSS sets handle height to 0, so we return 0
	const getEffectiveHandleHeight = () => {
		const isHeightReflow = window.innerHeight <= HEIGHT_REFLOW_BREAKPOINT;
		if (isHeightReflow) {
			return 0;
		}
		return handleHeight || DEFAULT_HANDLE_HEIGHT;
	};

	// Get the collapsed panel height for mobile (spacer + handle height)
	// This is the total height from handle TOP to container BOTTOM
	// At height-reflow, the handle has CSS height: 0, so we only count the spacer
	const getCollapsedPanelHeight = () => {
		return PANEL_SPACER_HEIGHT + getEffectiveHandleHeight();
	};

	// Extra pixels to expand beyond calculated minOffsetY to cover any header borders
	const FULLSCREEN_OVERFLOW_BUFFER = 2;

	/**
	 * Handle controller init
	 */
	vm.$onInit = () => {
		vm.disabled = vm.disabled === true || vm.disabled === 'true';
		destroy$ = new Subject<void>();
		if (vm.bgSplitterController.getId()) {
			handleId = 'resize-handle-' + vm.bgSplitterController.getId();
		}
		eventService.listen([
			EVENT_NAMES.FEEDBACK_SESSION_FEEDBACK_EXPANDED
		])
			.pipe(takeUntil(destroy$))
			.subscribe(() => {
				const bounds = vm.bgSplitterController.getBounds();
				setHandleHeightIfNotSet();
				const effectiveHeight = getEffectiveHandleHeight();
				const twoThirdsSplit = (bounds.height - effectiveHeight) / 3;
				const minSizeConstraint = bounds.height - effectiveHeight - vm.bgSplitterController.getMinSize();
				// Allow negative values so panel can expand over the header
				const minOffsetY = bounds.height - window.innerHeight - FULLSCREEN_OVERFLOW_BUFFER;
				// If minSize constraint is negative (not enough space), go to full expand instead
				const offsetY = minSizeConstraint < 0
					? minOffsetY
					: Math.min(twoThirdsSplit, minSizeConstraint);

				resize({
					offsetX: 0,
					offsetY
				});
			});
	};

	/**
	 * Handle post link
	 */
	vm.$postLink = () => {
		prev = angular.element($element[0].previousElementSibling);
		next = angular.element($element[0].nextElementSibling);

		// Add event listeners
		$element.on('mousedown', onMouseDown);
		angular.element(document).on('mousemove', onMouseMove);
		angular.element(document).on('mouseup', onMouseUp);
		$element.on('focusout', onFocusOut);
		$element.on('focusin', onFocusIn);
		accessibilityElem = $element[0].querySelector('.accessibility-text[aria-live="polite"]');

		$element.on('touchstart', onTouchStart);
		angular.element(document).on('touchmove', onTouchMove);
		angular.element(document).on('touchend', onTouchEnd);

		$element.on('resize-split-handle', onResize);
		angular.element(window).on('resize', onResize);
		angular.element(document).on('mouseout', onMouseOutWindow);

		// Accessibility listeners
		$element.on('keydown', onKeyDown);

		if (vm.bgSplitterController.isMobile) {
			const parentHeight = $element[0].parentElement.offsetHeight;
			const isHeightReflow = window.innerHeight <= HEIGHT_REFLOW_BREAKPOINT;
			const collapsedHeight = getCollapsedPanelHeight();

			// At height-reflow, start collapsed to maximize video space
			if (isHeightReflow) {
				resize({
					offsetX: 0,
					offsetY: parentHeight - collapsedHeight
				});
			} else {
				resize({
					offsetX: 0,
					offsetY: parentHeight / 3
				});
			}
		} else {
			resize(storage.get(handleId));
		}
	};

	/**
	 * Handle controller destroy
	 */
	vm.$onDestroy = () => {
		// Remove event listeners
		$element.off('mousedown', onMouseDown);
		angular.element(document).off('mousemove', onMouseMove);
		angular.element(document).off('mouseup', onMouseUp);
		$element.off('focusout', onFocusOut);
		$element.off('focusin', onFocusIn);

		$element.off('touchstart', onTouchStart);
		angular.element(document).off('touchmove', onTouchMove);
		angular.element(document).off('touchend', onTouchEnd);

		$element.off('resize-split-handle', onResize);
		angular.element(window).off('resize', onResize);
		angular.element(document).off('mouseout', onMouseOutWindow);

		$element.off('keydown', onKeyDown);

		if (destroy$) {
			destroy$.next();
			destroy$.complete();
		}
	};

	vm.clicked = () => {
		if (!vm.isDragging && vm.bgSplitterController.isMobile) {
			const currentPos = $element[0].offsetTop;
			const bounds = vm.bgSplitterController.getBounds();
			// Allow negative minOffsetY so panel can expand over the header
			const minOffsetY = bounds.height - window.innerHeight - FULLSCREEN_OVERFLOW_BUFFER;

			// Get the container's actual position in the viewport
			const containerRect = $element[0].parentElement.getBoundingClientRect();
			const collapsedPanelHeight = getCollapsedPanelHeight();

			// Calculate collapsed position to ensure handle is visible in viewport
			const maxVisibleCollapsedPos = window.innerHeight -
				containerRect.top - collapsedPanelHeight;
			const collapsedPos = Math.min(
				bounds.height - collapsedPanelHeight,
				maxVisibleCollapsedPos
			);

			setHandleHeightIfNotSet();
			const effectiveHeight = getEffectiveHandleHeight();
			const twoThirdsSplit = (bounds.height - effectiveHeight) / 3;

			let obj;

			// if fully expanded (at or near top of container) then collapse
			// Use Math.max(0, minOffsetY) to handle large window.innerHeight (e.g., in tests)
			// where minOffsetY becomes very negative but actual position clamps to 0
			if (currentPos <= Math.max(0, minOffsetY) + 5) {
				obj = {
					offsetX: 0,
					offsetY: collapsedPos
				};
			// else if collapsed (at or near collapsedPos) then go to midway
			} else if (currentPos >= collapsedPos - 5) {
				const minSizeConstraint = bounds.height - effectiveHeight - vm.bgSplitterController.getMinSize();
				// If minSize constraint is negative (not enough space), go to full expand instead
				if (minSizeConstraint < 0) {
					obj = {
						offsetX: 0,
						offsetY: minOffsetY
					};
				} else {
					obj = {
						offsetX: 0,
						offsetY: Math.min(twoThirdsSplit, minSizeConstraint)
					};
				}
			// else if in the middle (at or near midway) fully expand
			} else {
				obj = {
					offsetX: 0,
					offsetY: minOffsetY
				};
			}
			resize(obj);
		}
	};

	/**
	 * Trigger resize after element is completely destroyed
	 */
	$element.on('destroy', () => {
		vm.bgSplitterController.triggerResize();
	});

	function onFocusOut () {
		$scope.$evalAsync(() => {
			vm.grabbed = false;
			accessibilityElem.textContent = $translate.instant('bg-splitter_released');
		});
	}

	function onFocusIn () {
		accessibilityElem.textContent = '';
	}

	/**
	 * Resize
	 *
	 * @param evt - position data or event
	 * @param isWindowResize - true if triggered by window resize (not user interaction)
	 */
	function resize (evt, isWindowResize = false) {

		$scope.$evalAsync(() => {
			// get orientation
			// we can get the last orientation here if we call
			// it before we call bgSplitterController.update
			const lastOrientation = vm.bgSplitterController.getOrientation();

			// update the bounds and orientation
			vm.bgSplitterController.update();

			// get container bounds
			const bounds = vm.bgSplitterController.getBounds();

			// get x and y offsets, if none provided
			// default to the current offset
			// IMPORTANT: Use != null check instead of truthy check because 0 is a valid value
			const coordinate = {
				offsetX: evt && evt.offsetX != null ? evt.offsetX : $element[0].offsetLeft,
				offsetY: evt && evt.offsetY != null ? evt.offsetY : $element[0].offsetTop
			};

			// after the orientation has changed
			// reset width and height properties
			if (lastOrientation !== vm.bgSplitterController.getOrientation()) {
				angular.forEach([$element, prev, next], (item) => {
					item.css(clearStyles);
				});
			}

			if (vm.bgSplitterController.isLandscape()) {
				if (!handleWidth) {
					handleWidth = outerWidth($element[0]);
				}

				const min = vm.bgSplitterController.getMinSize();
				const max = bounds.width - handleWidth - vm.bgSplitterController.getMinSize();
				if (min > (bounds.width - handleWidth) / 2) {
					// split evenly to avoid jittering back and forth
					coordinate.offsetX = (bounds.width - handleWidth) / 2;
				} else {
					if (coordinate.offsetX < min) {
						coordinate.offsetX = min;
					} else if (coordinate.offsetX > max) {
						coordinate.offsetX = max;
					}
				}

				$element.css({
					height: '100%',
					left: coordinate.offsetX + 'px',
					zIndex: 'auto'
				});

				prev.css({
					top: 0,
					bottom: 0,
					left: 0,
					height: 'auto',
					width: coordinate.offsetX + 'px'
				});

				const nextLeft = coordinate.offsetX + handleWidth;
				next.css({
					top: 0,
					bottom: 0,
					right: 0,
					left: nextLeft + 'px',
					height: 'auto',
					width: bounds.width - nextLeft + 'px',
					zIndex: 'auto'
				});

				updateSeparatorValueAttributes(
					coordinate.offsetX,
					bounds.width
				);

			} else {
				setHandleHeightIfNotSet();

				const min = vm.bgSplitterController.getMinSize();
				const max = bounds.height - handleHeight - vm.bgSplitterController.getMinSize();
				// Calculate the minimum possible offsetY (fully expanded position)
				// Allow negative values so panel can expand over the header
				const minOffsetY = bounds.height - window.innerHeight - FULLSCREEN_OVERFLOW_BUFFER;

				// Get the container's actual position in the viewport
				const containerRect = $element[0].parentElement.getBoundingClientRect();
				const collapsedPanelHeight = getCollapsedPanelHeight();

				// Calculate collapsed position to ensure handle is visible in viewport
				const maxVisibleCollapsedPos = window.innerHeight - containerRect.top - collapsedPanelHeight;
				const collapsedPos = Math.min(
					bounds.height - collapsedPanelHeight,  // Original calculation (relative to container)
					maxVisibleCollapsedPos  // Ensure visible in viewport with buffer
				);

				if (vm.bgSplitterController.isMobile) {
					const isHeightReflow = window.innerHeight <= HEIGHT_REFLOW_BREAKPOINT;
					const isPanelCurrentlyClosed = !ngxFeedbackSessionService.getIsPanelOpen();

					// In height-reflow mode with panel closed during WINDOW RESIZE, always snap to collapsed position
					// This prevents bounds changes during resize from accidentally opening the panel
					// But allow user interactions (click, drag) to expand the panel
					if (isWindowResize && isHeightReflow && isPanelCurrentlyClosed) {
						coordinate.offsetY = collapsedPos;
						ngxFeedbackSessionService.togglePanel(false);
					} else if (coordinate.offsetY < minOffsetY) {
						// Clamp to min (fully expanded) - can be negative for fullscreen over header
						coordinate.offsetY = minOffsetY;
						ngxFeedbackSessionService.togglePanel(true);
					} else if (dragging && coordinate.offsetY > collapsedPos) {
						// During drag: clamp to collapsed position to prevent dragging past bottom
						coordinate.offsetY = collapsedPos;
						ngxFeedbackSessionService.togglePanel(false);
					} else {
						// Normal mobile mode: track panel state based on position
						const isNearCollapsed = coordinate.offsetY >= collapsedPos - 10;
						ngxFeedbackSessionService.togglePanel(!isNearCollapsed);
					}
				} else {
					if (min > (bounds.height - handleHeight) / 2) {
						// split evenly to avoid jittering back and forth
						coordinate.offsetY = (bounds.height - handleHeight) / 2;
					} else {
						if (coordinate.offsetY < min) {
							coordinate.offsetY = min;
						} else if (coordinate.offsetY > max) {
							coordinate.offsetY = max;
						}
					}
				}

				$element.css({
					width: '100%',
					top: coordinate.offsetY + 'px',
					zIndex: vm.bgSplitterController.isMobile ? 100 : 'auto'
				});

				prev.css({
					left: 0,
					right: 0,
					top: 0,
					width: 'auto',
					height: coordinate.offsetY + 'px',
					zIndex: vm.bgSplitterController.isMobile ? 1 : 'auto'
				});

				const nextTop = coordinate.offsetY + getEffectiveHandleHeight();
				next.css({
					left: 0,
					right: 0,
					top: nextTop + 'px',
					width: 'auto',
					height: bounds.height - nextTop + 'px',
					zIndex: vm.bgSplitterController.isMobile ? 2 : 'auto'
				});

				updateSeparatorValueAttributes(
					coordinate.offsetY,
					bounds.height
				);
			}

			// ignore storing handle coordinates when the
			// handle is not visible
			const handleBounds = $element[0].getBoundingClientRect();
			if (handleBounds.height <= 0 || handleBounds.width <= 0) {
				return;
			}

			// store position
			if (handleId) {
				storage.put(handleId, {
					offsetX: coordinate.offsetX,
					offsetY: coordinate.offsetY
				});
			}
		});
	}

	/**
	 * Get outer width
	 *
	 * @param el
	 * @returns {number}
	 */
	function outerWidth (el) {
		let width = el.offsetWidth;
		const style = getComputedStyle(el);

		width += parseInt(style.marginLeft, 10) + parseInt(style.marginRight, 10);
		return width;
	}

	function setHandleHeightIfNotSet () {
		if (!handleHeight) {
			handleHeight = outerHeight($element[0]);
		}
	}

	function updateSeparatorValueAttributes (current: number, totalSize: number) {
		const clampedCurrent = Math.min(Math.max(current, 0), totalSize);
		const normalizedValue = (clampedCurrent / totalSize) * 100;

		vm.ariaValueNow = Number(normalizedValue.toFixed(2));
	}

	/**
	 * Get outer height
	 *
	 * @param el
	 * @returns {number}
	 */
	function outerHeight (el) {
		let height = el.offsetHeight;
		const style = getComputedStyle(el);

		height += parseInt(style.marginTop, 10) + parseInt(style.marginBottom, 10);
		return height;
	}

	/**
	 * On mouse down event handler
	 *
	 * @param evt
	 */
	function onMouseDown (evt) {
		if (vm.disabled) {
			return;
		}

		$scope.$evalAsync(() => {
			evt.preventDefault();
			vm.isDragging = false;

			const bounds = $element[0].getBoundingClientRect();
			vm.grabbed = true;

			// capture drag offsets
			dragging = {
				offsetX: evt.clientX - bounds.left,
				offsetY: evt.clientY - bounds.top
			};
		});
	}

	/**
	 * On mouse move event handler
	 *
	 * @param evt
	 */
	function onMouseMove (evt) {
		if (!dragging) { return; }

		$scope.$evalAsync(() => {
			vm.isDragging = true;
			const bounds = vm.bgSplitterController.getBounds();

			resize({
				offsetX: evt.clientX - bounds.left - dragging.offsetX,
				offsetY: evt.clientY - bounds.top - dragging.offsetY
			});

			// tell container to resize all children
			vm.bgSplitterController.triggerResize();
		});
	}

	/**
	 * On mouse up event handler
	 *
	 * @param evt
	 */
	function onMouseUp () {
		dragging = null;
		vm.grabbed = false;
		vm.isDragging = false;
	}

	function onKeyDown (event: JQueryEventObject) {
		if (vm.disabled) {
			return;
		}

		$scope.$evalAsync(() => {
			// Get orientation info
			const orientation = vm.bgSplitterController.getOrientation();
			const { left, right, grabSpeech } = orientation === ORIENTATION.LANDSCAPE ?
				{ left: 'ArrowLeft', right: 'ArrowRight', grabSpeech: 'bg-splitter_grabbed-horizontal' } :
				{ left: 'ArrowUp', right: 'ArrowDown', grabSpeech: 'bg-splitter_grabbed-vertical' };

			// Update grabbed status
			if (event.key === ' ' || event.key === 'Enter') {
				vm.grabbed = !vm.grabbed;
				accessibilityElem.textContent = vm.grabbed ?
					$translate.instant(grabSpeech) :
					$translate.instant('bg-splitter_released');
			} else {
				accessibilityElem.textContent = '';
			}

			// Move left or right while grabbed
			if (vm.grabbed && (event.key === left || event.key === right)) {
				const offsets = {} as any;
				const direction = event.key === left ? -1 : 1;

				if (orientation === ORIENTATION.LANDSCAPE) {
					offsets.offsetX = $element[0].offsetLeft + (10 * direction);
				} else {
					offsets.offsetY = $element[0].offsetTop + (10 * direction);
				}

				accessibilityElem.textContent = $translate.instant(getAccessibleMoveText(direction, orientation));
				resize(offsets);

				// tell container to resize all children
				vm.bgSplitterController.triggerResize();
			}
		});
	}

	function getAccessibleMoveText (direction: number, orientation: ORIENTATION) {
		if (orientation === ORIENTATION.LANDSCAPE && direction === -1) {
			return 'bg-splitter_moved-horizontal-left';
		} else if (orientation === ORIENTATION.LANDSCAPE) {
			return 'bg-splitter_moved-horizontal-right';
		} else if (orientation === ORIENTATION.PORTRAIT && direction === -1) {
			return 'bg-splitter_moved-vertical-up';
		} else {
			return 'bg-splitter_moved-vertical-down';
		}
	}

	/**
	 * On touch start event
	 *
	 * @param evt
	 */
	function onTouchStart (evt) {
		const touch = getTouches(evt)[0];
		touch.preventDefault = angular.noop;
		onMouseDown(touch);
	}

	/**
	 * On touch move event
	 *
	 * @param evt
	 */
	function onTouchMove (evt) {
		if (!dragging) { return; }
		const touch = getTouches(evt)[0];
		onMouseMove(touch);
	}

	/**
	 * On touch end event handler
	 *
	 * @param evt
	 */
	function onTouchEnd () {
		onMouseUp();
	}

	/**
	 * On resize event handler
	 */
	function onResize (evt) {
		evt.preventDefault();

		// Handle height-reflow transitions and state maintenance for mobile
		if (vm.bgSplitterController.isMobile) {
			const isNowInHeightReflow = window.innerHeight <= HEIGHT_REFLOW_BREAKPOINT;
			const bounds = vm.bgSplitterController.getBounds();

			// Guard: skip if bounds aren't valid (can happen during component initialization)
			if (!bounds || bounds.height <= 0) {
				return;
			}

			const collapsedHeight = getCollapsedPanelHeight();

			// Get the container's actual position in the viewport
			const containerRect = $element[0].parentElement.getBoundingClientRect();

			// Calculate collapsed position to ensure handle is visible in viewport
			const maxVisibleCollapsedPos = window.innerHeight - containerRect.top
				- collapsedHeight;
			const collapsedPos = Math.min(
				bounds.height - collapsedHeight,
				maxVisibleCollapsedPos
			);

			// Detect transition into/out of height-reflow mode
			if (isNowInHeightReflow !== wasInHeightReflow) {
				wasInHeightReflow = isNowInHeightReflow;

				// Reset cached handle dimensions - they may differ at different breakpoints
				handleHeight = null;
				handleWidth = null;

				if (isNowInHeightReflow) {
					// Transitioning INTO height-reflow: collapse the panel
					resize({ offsetX: 0, offsetY: collapsedPos }, true);
				} else {
					// Transitioning OUT OF height-reflow: expand to 1/3 split
					setHandleHeightIfNotSet();
					resize({ offsetX: 0, offsetY: bounds.height / 3 }, true);
				}
				return;
			}

			// In height-reflow mode with panel closed: maintain collapsed position
			// Skip if user is actively dragging the handle
			const isPanelClosed = !ngxFeedbackSessionService.getIsPanelOpen();
			if (isNowInHeightReflow && isPanelClosed && !dragging) {
				resize({ offsetX: 0, offsetY: collapsedPos }, true);
				return;
			}
		}

		resize(evt, true);
	}

	/**
	 * On mouse out of window
	 * Stop dragging when mouse leaves window
	 */
	function onMouseOutWindow (evt) {
		evt = evt ? evt : $window.event;
		const from = evt.relatedTarget || evt.toElement;
		if (!from || from.nodeName === 'HTML') {
			onMouseUp();
		}
	}

	/**
	 * Returns an array of touch changes
	 *
	 * @param evt
	 * @returns {*}
	 */
	function getTouches (evt) {
		let touches;

		if (evt.originalEvent) {
			if (evt.originalEvent.touches) {
				touches = evt.originalEvent.touches;
			} else if (evt.originalEvent.changedTouches) {
				touches = evt.originalEvent.changedTouches;
			}
		} else if (evt.touches) {
			touches = evt.touches;
		}

		return touches;
	}
};
